import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Search, X } from "lucide-react";
import { AREAS, KERN_COUNT, UMLAND_COUNT } from "@/data/areas";
import AreaMap3D from "@/components/AreaMap3D";
import { Label } from "@/components/Shared";

const norm = (s) =>
  s.toLowerCase().replace(/ä/g, "a").replace(/ö/g, "o").replace(/ü/g, "u").replace(/ß/g, "ss");

const AreaScanner = () => {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);

  const handleSelect = useCallback((a) => {
    setActive(a);
    setQ("");
  }, []);

  const matches = useMemo(() => {
    if (!q.trim()) return null;
    const n = norm(q.trim());
    return AREAS.filter((a) => norm(a.n).includes(n));
  }, [q]);

  const hit = matches && matches.length ? matches[0] : null;
  const shown = active || hit;

  return (
    <div data-testid="area-scanner" className="grid gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:gap-14">
      {/* ---- left: search + result ---- */}
      <div>
        <Label>/ Stadtgebiet-Scanner</Label>
        <h2 className="mt-4 text-[26px] font-bold leading-[1.15] sm:text-[32px] lg:text-[38px]">
          Liegt Ihr Objekt in unserem Einsatzgebiet?
        </h2>
        <p className="mt-5 text-[15px] leading-relaxed text-[#66788A]">
          {KERN_COUNT} Stadt- und Ortsteile in Bremen plus {UMLAND_COUNT} Orte im direkten
          Umland. Geben Sie Ihren Stadtteil oder Ort ein oder tippen Sie direkt auf die Karte – der Scanner zeigt sofort den Status.
        </p>

        <div className="relative mt-7">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#66788A]" />
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(null);
            }}
            data-testid="scanner-input"
            placeholder="z. B. Hemelingen, Vegesack, Stuhr …"
            aria-label="Stadtteil oder Ort suchen"
            className="w-full rounded-full border border-[#DCE5EF] bg-white py-3.5 pl-11 pr-11 text-[14.5px] outline-none transition-colors placeholder:text-[#9AA9B8] focus:border-[#0757B8]"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              aria-label="Suche löschen"
              data-testid="scanner-clear"
              className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-[#F5F8FC] text-[#66788A] hover:text-[#10253A]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* result */}
        <div className="mt-5 min-h-[112px]" aria-live="polite">
          {q && !hit && (
            <div data-testid="scanner-miss" className="rounded-2xl border border-[#DCE5EF] bg-white p-5">
              <p className="text-[14px] font-semibold text-[#10253A]">Kein Treffer in der Liste</p>
              <p className="mt-1.5 text-[13px] leading-relaxed text-[#66788A]">
                Das heißt nicht automatisch nein. Fragen Sie an – wir prüfen, ob sich Ihr Objekt
                in die Tourenplanung einfügen lässt.
              </p>
            </div>
          )}
          {shown && (
            <div
              data-testid="scanner-result"
              className={`rounded-2xl p-5 ${shown.zone === "kern" ? "bg-[#0757B8] text-white" : "bg-[#062B52] text-white"}`}
            >
              <p className="font-mono-label text-[#FF6B00]">
                {shown.zone === "kern" ? "Kerngebiet" : "Umland · auf Anfrage"}
              </p>
              <p className="mt-2 flex items-center gap-2 text-[19px] font-bold" style={{ color: "#fff" }}>
                <MapPin className="h-4 w-4 text-[#FF6B00]" /> {shown.n}
              </p>
              <p className="mt-2 text-[13px] leading-relaxed text-white/75">
                {shown.zone === "kern"
                  ? "Liegt im Bremer Stadtgebiet – hier planen wir feste Touren mit verlässlichen Zeitfenstern."
                  : "Direktes Umland: Machbarkeit hängt von Fläche, Objektart und Tourenplanung ab – wir geben Ihnen eine offene Einschätzung."}
              </p>
              <Link
                to="/kontakt"
                data-testid="scanner-cta"
                className="btn btn-orange mt-4 h-11 px-5 text-[13px]"
              >
                Für {shown.n} anfragen <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          )}
          {!q && !active && (
            <div className="flex flex-wrap gap-2">
              {["Hemelingen", "Vegesack", "Schwachhausen", "Huchting", "Stuhr"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setQ(s)}
                  data-testid={`scanner-quick-${s.toLowerCase()}`}
                  className="rounded-full border border-[#DCE5EF] bg-white px-3.5 py-2 text-[12.5px] font-medium text-[#10253A] transition-colors hover:border-[#0757B8] hover:text-[#0757B8]"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-[#DCE5EF] pt-6 text-[12.5px]">
          <span className="text-[#66788A]">
            Hauptbüro Flughafenallee · Fuhrpark-Start Hemelingen
          </span>
          <Link
            to="/einsatzgebiet"
            data-testid="scanner-area-link"
            className="ml-auto inline-flex items-center gap-1.5 font-semibold text-[#0757B8] hover:text-[#FF6B00]"
          >
            Einsatzgebiet ansehen <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      {/* ---- right: echte 3D-Karte ---- */}
      <div>
        <AreaMap3D
          focus={shown}
          matches={matches}
          onSelect={handleSelect}
        />
        <p className="mt-2.5 text-[11px] leading-relaxed text-[#66788A]">
          Echte Kartendarstellung (OpenStreetMap) mit 3D-Kameraperspektive. Mit Strg/⌘ + Scrollen
          zoomen, ziehen zum Drehen und Verschieben.
        </p>
      </div>
    </div>
  );
};

export default AreaScanner;
